import { ReactElement, forwardRef } from 'react';
import { world } from '../../../languages';
import cn from '../../../utils/classnames';
import toKebabCase from '../../../utils/to-kebab-case';
import { useOverlayTriggerContext } from '../../overlay-trigger/overlay-trigger-context';
import getInitialStyles from '../utils/get-initial-styles';
import { TooltipProps } from './types';
import './tooltip.scss';

const Tooltip = forwardRef<HTMLDivElement, TooltipProps>((props, ref): ReactElement | null => {
    const { show, children, hasDoneInitialMeasure, popper, placement, className, style, autoWidth, id, ...rest } =
        props;

    // Overlay injects arrowProps for arrow-rendering overlays; tooltips have no arrow, so drop it before spreading to the DOM
    const { arrowProps, ...tooltipProps } = rest as typeof rest & { arrowProps?: unknown };

    const overlayTrigger = useOverlayTriggerContext();

    if (!children) {
        return null;
    }

    const shouldGetInitialStyles = show && !hasDoneInitialMeasure;
    const initialStyles = shouldGetInitialStyles ? getInitialStyles(popper?.strategy) : {};
    const styles = { ...style, ...initialStyles };

    const idInKebabCase = id !== undefined ? toKebabCase(id) : undefined;
    const role = 'tooltip';
    const classNames = cn('tooltip', className, `tooltip-${placement}`, { 'auto-width': autoWidth });

    const handlers = overlayTrigger?.shouldTriggerOnHover
        ? { onMouseEnter: overlayTrigger.handleShow, onMouseLeave: overlayTrigger.handleHideIfTriggeredByHover }
        : {};

    return (
        <div
            ref={ref}
            id={idInKebabCase}
            data-testid={idInKebabCase}
            role={role}
            style={styles}
            className={classNames}
            {...handlers}
            {...tooltipProps}
        >
            <div className='tooltip-inner' lang={world}>
                {children}
            </div>
        </div>
    );
});

Tooltip.displayName = 'Tooltip';

export default Tooltip;
